import { loadTagFeedbackTooltip, setTagFeedbackTooltip } from "@/common/utils/storage";
import { useEffect, useMemo, useState } from "react";
import { useBodyLock } from "@/common/hooks/useBodyLock";
import { CurrentUserObject } from "@/common/hooks/useVisitorInfo";

interface IUseDismissTagFeedbackTooltip {
  position: number;
  isVisible: boolean;
  gifId: string;
  currentUser: CurrentUserObject | null;
}

export const useDismissTagFeedbackTooltip = ({
  position,
  isVisible,
  gifId,
  currentUser
}: IUseDismissTagFeedbackTooltip) => {
  const [tooltipWasShown, setTooltipWasShown] = useState<boolean>(loadTagFeedbackTooltip());
  const [visible, setVisible] = useState<boolean>(false);

  const visibleObserver = useMemo(() => {
    return new IntersectionObserver(([entry]) => {
      setVisible(entry.isIntersecting);
    }, {
      threshold: 0,
      rootMargin: "-200px 0px -200px 0px",
    });
  }, []);

  useEffect(() => {
    const el = document.querySelector<HTMLDivElement>(`#gif_${gifId} .tagList`);

    if (!el || !isVisible) {
      return;
    }

    visibleObserver.observe(el);

    return () => {
      visibleObserver.disconnect();
    };
  }, [visibleObserver, isVisible]);

  const onDismissTagFeedbackTooltip = (forceClose: boolean) => {
    if (forceClose) {
      setTooltipWasShown(true);
      return;
    }

    setTagFeedbackTooltip(true);
    setTooltipWasShown(true);
  };

  const isShowTagFeedbackTooltip = useMemo(
    () => position === 2 && !tooltipWasShown && visible && !!currentUser,
    [position, tooltipWasShown, visible, currentUser]
  );

  useBodyLock(isShowTagFeedbackTooltip);

  return {
    isShowTagFeedbackTooltip,
    onDismissTagFeedbackTooltip,
  };
};
